import { z } from 'zod'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { usePageInfo } from 'app/frontend/base/config/queries'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import { zSetting } from 'app/frontend/base/helpers/forms'
import { useQueryClient } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

const formSchema = z
  .object({
    settings: z.object({
      battletagVisibility: zSetting(z.string()),
      altVisibility: zSetting(z.string()),
      allowCharactersBeingTracked: zSetting(z.string()),
    }),
    nickname: z.string().max(20).or(z.literal('')).or(z.null()).optional(),
  })
  .superRefine((data, context) => {
    const nickname = data.nickname?.trim() ?? ''
    const usesCustomNickname =
      data.settings.battletagVisibility.value === 'custom_nickname'

    if ((usesCustomNickname || nickname.length > 0) && nickname.length < 2) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nickname'],
        message: 'Nickname must be between 2 and 20 characters',
      })
    }
  })

const NicknameField = () => {
  const { watch } = useFormContext<z.infer<typeof formSchema>>()
  const usesCustomNickname =
    watch('settings.battletagVisibility.value') === 'custom_nickname'

  if (!usesCustomNickname) return null

  return <TextField name='nickname' />
}

const UserSettingsPrivacySection = () => {
  const queryClient = useQueryClient()
  const {
    data: { user },
  } = usePageInfo()

  if (!user) return null

  return (
    <FormWrapper
      formSchema={formSchema}
      entityType='user'
      entity={user}
      onSave={async (form, data) => {
        form.reset(data)
        await queryClient.invalidateQueries({ queryKey: ['pageInfo'] })
      }}
    >
      <SettingField name='battletagVisibility' />
      <NicknameField />
      <SettingField name='altVisibility' />
      <SettingField name='allowCharactersBeingTracked' />
    </FormWrapper>
  )
}

export default UserSettingsPrivacySection
